import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { MatDialog } from '@angular/material/dialog';
import { ProblemDevicesModalComponent } from './problem-devices-modal/problem-devices-modal.component';
import { ProblemOrdersModalComponent } from './problem-orders-modal/problem-orders-modal.component';

@Component({
  selector: 'app-back-office24-start',
  standalone: false,
  templateUrl: './back-office24-start.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './back-office24-start.component.css'
})
export class BackOffice24StartComponent implements OnInit, OnDestroy {
  @Input() organization: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();
  isLoading: boolean = false;
  activeRequests: number = 0;
  orderNrForm: UntypedFormGroup;
  problemFlightsNumber: number = 0;
  unPlannedTaskNumber: number = 0;
  overlappingParkingsNumber: number = 0;
  problemDevicesNumber: number = 0;
  problemDevices: any[] = [];
  showProblemDevices: boolean = false;
  shuttlesNotInScheduleNumber: number = 0;
  problemOrders: { orderId: string, orderNr: number | null, carId: string | null, problemFlags: number }[] = [];
  problemOrdersNumber: number = 0;
  readonly flygetsOrganizationId = 'a848ccd3-fd8d-4035-925e-571ebb4e93e8';

  readonly i18n = {
    problemFlights: { en: 'Problem Flights', sv: 'Problemflyg' },
    unplannedTasks: { en: 'Unplanned Tasks', sv: 'Oplanerade uppgifter' },
    problemParkings: { en: 'Problem Parkings', sv: 'Problemparkeringar' },
    problemDevices: { en: 'Problem Devices', sv: 'Problemenheter' },
    problemShuttles: { en: 'Problem Shuttles', sv: 'Problemskyttlar' },
    problemOrders: { en: 'Problem Orders', sv: 'Problemordrar' },
  };

  componentDestroyed$: Subject<boolean> = new Subject();

  constructor(
    private backOfficeService: BackOfficeService,
    private router: Router,
    private fb: UntypedFormBuilder,
    private backOffice24StatesService: BackOffice24StatesService,
    private dialog: MatDialog) {
  }

  setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  incrementRequests() {
    this.activeRequests++;
    if (this.activeRequests > 0) {
      this.isLoading = true;
    }
  }

  decrementRequests() {
    if (this.activeRequests > 0) {
      this.activeRequests--;
    }
    if (this.activeRequests <= 0) {
      this.isLoading = false;
    }
  }


  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => {
        this.organization = org;
        if (this.organization?.organizationId) {
          this.loadAllProblems();
        }
      }
    );
    this.setOrderNrForm();
  }

  setOrderNrForm() {
    this.orderNrForm = this.fb.group({
      orderNr: '',
      searchSelect: ['O']
    });
  }

  private loadAllProblems() {
    if (!this.organization) return;

    this.incrementRequests();
    this.backOfficeService.getDashboardState(this.organization.organizationId).pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (problems: any) => {
          this.problemFlightsNumber = problems?.problemFlightsCount || 0;
          this.unPlannedTaskNumber = problems?.unplannedTaskCount || 0;
          this.overlappingParkingsNumber = problems?.overlappedParkingsCount || 0;
          this.showProblemDevices = problems?.problemDevices != null;
          this.problemDevices = problems?.problemDevices || [];
          this.problemDevicesNumber = this.problemDevices.length;
          this.shuttlesNotInScheduleNumber = problems?.shuttlesNotInScheduleList?.length || 0;
          this.problemOrders = problems?.problemOrders || [];
          this.problemOrdersNumber = this.problemOrders.length;
          this.decrementRequests();
        },
        error: (error) => {
          this.decrementRequests();
          console.error('Error fetching problems:', error);
        }
      });
  }

  redirectToHandleFlights() {
    this.router.navigate([this.router.url, 'dashboard'], { queryParams: { tab: 'flights' } });
  }

  redirectToAdditionalServiceTasks() {
    this.router.navigate([this.router.url, 'dashboard'], { queryParams: { tab: 'unplanned' } });
  }

  redirectToOverlappingParkings() {
    this.router.navigate([this.router.url, 'parking'], { queryParams: { tab: 'overlapping' } });
  }

  redirectToShuttlesNotInSchedule() {
    this.router.navigate([this.router.url, 'route-schedules']);
  }

  openProblemDevicesModal(): void {
    this.dialog.open(ProblemDevicesModalComponent, {
      maxWidth: '95vw',
      hasBackdrop: true,
      data: {
        problemDevices: this.problemDevices
      }
    });
  }

  openProblemOrdersModal(): void {
    this.dialog.open(ProblemOrdersModalComponent, {
      maxWidth: '95vw',
      hasBackdrop: true,
      data: {
        problemOrders: this.problemOrders,
        onOrderSolved: (orderId: string) => {
          this.problemOrders = this.problemOrders.filter(o => o.orderId !== orderId);
          this.problemOrdersNumber = this.problemOrders.length;
        }
      }
    });
  }

  getOrderNr() {
    var orderNr = this.orderNrForm.controls['orderNr'].value;

    if (this.orderNrForm.controls['searchSelect'].value == 'O') {
      this.backOfficeService
        .getOrderNr(orderNr)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(order => {
          this.goToOrder(order.orderId);
        });
    }

    if (this.orderNrForm.controls['searchSelect'].value == 'T') {
      this.backOfficeService
        .getTransactionNr(orderNr)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(transaction => {
          this.goToOrder(transaction.orderId);
        });
    }

    if (this.orderNrForm.controls['searchSelect'].value == 'C') {
      this.backOfficeService
        .getOrderParkingCarId(orderNr, this.organization.organizationId)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(orderParkingVM => {
          this.goToOrder(orderParkingVM.orderId);
        });
    }
  }

  goToOrder(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.orgSubscription.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }



}
